import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, track } from "@/lib/api";
import { MerchantNote, labelNamesMerchant } from "./MerchantNote";
import { AffiliateCardLink, AffiliateCardButton } from "./AffiliateCardLink";
import { SmartImage } from "@/components/site/SmartImage";

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const pick = (offers, name) =>
  (name ? offers.find((o) => o.display_name === name) : offers[0]) || null;

const PathCta = ({ cta, offers, placement, dark, testId, asSpan }) => {
  if (cta.to)
    return (
      <Link
        to={cta.to}
        data-testid={testId}
        className="inline-flex items-center gap-2 px-6 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
      >
        {cta.label} <ArrowRight size={15} />
      </Link>
    );

  const offer = pick(offers[cta.category] || [], cta.display_name);
  if (!offer) return null;

  // Inside a card that is itself the single affiliate link, the CTA renders as a styled span so no
  // nested anchor exists and one click reports one conversion.
  if (asSpan)
    return (
      <div>
        <AffiliateCardButton testId={testId}>
          {cta.label} <ArrowUpRight size={15} />
        </AffiliateCardButton>
        <MerchantNote merchant={offer.merchant} dark={dark} testId={`${testId}-merchant`} />
      </div>
    );

  return (
    <div>
      <a
        href={offer.tracking_url}
        target="_blank"
        rel="sponsored nofollow noopener"
        data-testid={testId}
        onClick={() =>
          track({
            event_type: "cta_click",
            cta_placement: placement,
            offer_category: cta.category,
            offer_id: offer.id,
            merchant: offer.merchant,
          })
        }
        className="inline-flex items-center gap-2 px-6 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
      >
        {cta.label} <ArrowUpRight size={15} />
      </a>
      <MerchantNote merchant={offer.merchant} dark={dark} testId={`${testId}-merchant`} />
    </div>
  );
};

/**
 * Several merchants under one product option. Each choice leads with the merchant name and carries
 * one short factual line, so the option stays the heading and the merchants stay secondary.
 */
const MerchantChoice = ({ option, offers, placement, testId }) => {
  const offer = pick(offers[option.category] || [], option.display_name);
  if (!offer) return null;
  const named = labelNamesMerchant(option.label, offer.merchant);

  return (
    <div data-testid={testId}>
      <a
        href={offer.tracking_url}
        target="_blank"
        rel="sponsored nofollow noopener"
        data-testid={`${testId}-link`}
        onClick={() =>
          track({
            event_type: "cta_click",
            cta_placement: placement,
            offer_category: option.category,
            offer_id: offer.id,
            merchant: offer.merchant,
          })
        }
        className="inline-flex items-center gap-2 px-5 py-3 bg-sage text-white text-[12px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
      >
        {option.label} <ArrowUpRight size={14} />
      </a>
      <p className="text-[14.5px] leading-[1.55] text-[#454545] mt-2.5">
        {option.note}
        <span className="text-[12px] text-[#6E6A63] block mt-1">
          {named ? "Affiliate link" : `Affiliate link · opens at ${offer.merchant}`}
        </span>
      </p>
    </div>
  );
};

/**
 * A card's own photograph doubles as the affiliate link when the card opts in. The merchant still
 * comes from the offer record, and the CTA underneath names it, so nothing is hidden behind the
 * image.
 */
const CardImage = ({ card, offers }) => {
  const box = `aspect-[16/10] overflow-hidden ${card.fit === "contain" ? "bg-[#F4F2ED]" : ""}`;
  const img = (
    <SmartImage
      src={card.image}
      alt={card.image_alt}
      sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
      maxWidth={768}
      className={`w-full h-full ${
        card.fit === "contain" ? "object-contain p-4" : "object-cover"
      } transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]`}
    />
  );

  const offer = card.image_cta && card.cta?.category
    ? pick(offers[card.cta.category] || [], card.cta.display_name)
    : null;
  if (!offer) return <div className={box}>{img}</div>;

  return (
    <a
      href={offer.tracking_url}
      target="_blank"
      rel="sponsored nofollow noopener"
      aria-label={`${card.label} — explore at ${offer.merchant}`}
      data-testid={`hub-card-image-link-${slugify(card.label)}`}
      onClick={() =>
        track({
          event_type: "cta_click",
          cta_placement: "HUB CARD IMAGE",
          offer_category: card.cta.category,
          offer_id: offer.id,
          merchant: offer.merchant,
        })
      }
      className={`group block ${box}`}
    >
      {img}
    </a>
  );
};

const PathGroup = ({ group, offers }) => {
  const dark = group.tone === "dark";
  return (
    <section
      id={group.id}
      data-testid={`hub-paths-${group.id}`}
      className={`mt-12 ${dark ? "bg-charcoal text-white px-6 md:px-9 py-9" : ""}`}
    >
      <span className={dark ? "eyebrow eyebrow-light" : "eyebrow"}>{group.heading}</span>
      {group.intro && (
        <p
          className={`text-[16.5px] md:text-[17.5px] leading-[1.6] mt-4 max-w-3xl ${
            dark ? "text-[#D7D4CF]" : "text-[#3F3F3F]"
          }`}
        >
          {group.intro}
        </p>
      )}

      {group.layout === "feature" && group.cards?.length > 0 && (
        <div className="mt-7 space-y-px bg-line">
          {group.cards.map((c) => (
            <div
              key={c.label}
              data-testid={`hub-feature-${slugify(c.label)}`}
              className="bg-white"
            >
              <div className="h-[260px] md:h-[420px] overflow-hidden">
                <SmartImage
                  src={c.image}
                  alt={c.image_alt}
                  sizes="100vw"
                  maxWidth={1600}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="px-6 md:px-9 py-7 md:py-9">
                <h3 className="text-[21px] md:text-[24px] font-medium text-charcoal">{c.label}</h3>
                <p className="text-[15.5px] md:text-[16.5px] leading-[1.6] text-[#454545] mt-3 max-w-3xl">
                  {c.note}
                </p>
                {c.options?.length > 0 && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-7 mt-8">
                    {c.options.map((o) => (
                      <MerchantChoice
                        key={o.label}
                        option={o}
                        offers={offers}
                        placement="HUB MERCHANT CHOICE"
                        testId={`hub-merchant-${slugify(o.label)}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {group.layout !== "feature" && group.cards?.length > 0 && (
        <div
          className={`grid gap-px bg-line mt-7 ${
            group.cards.length === 1
              ? "max-w-3xl"
              : group.cards.length === 3
              ? "md:grid-cols-2 lg:grid-cols-3"
              : "md:grid-cols-2"
          }`}
        >
          {group.cards.map((c, idx) => {
            // On tablets a three-card row falls to two columns, so the odd card widens across the
            // second row instead of leaving an empty cell beside it.
            const span =
              group.cards.length === 3 && idx === 2 ? "md:col-span-2 lg:col-span-1" : "";
            // One affiliate destination and nothing else to choose => the whole card is that link.
            const soleOffer =
              c.cta && !c.cta.to && !c.options?.length
                ? pick(offers[c.cta.category] || [], c.cta.display_name)
                : null;
            const body = (
              <>
                <CardImage card={c} offers={soleOffer ? {} : offers} />
                <div className="px-6 md:px-7 py-6 flex flex-col flex-1">
                  <h3 className="text-[19px] md:text-[20px] font-medium text-charcoal">{c.label}</h3>
                  <p className="text-[15.5px] md:text-[16px] leading-[1.6] text-[#454545] mt-2.5">
                    {c.note}
                  </p>
                  {c.cta && (
                    <div className="mt-auto pt-6">
                      <PathCta
                        cta={c.cta}
                        offers={offers}
                        placement="HUB SAUNA CARD"
                        asSpan={Boolean(soleOffer)}
                        testId={`hub-card-cta-${slugify(c.label)}`}
                      />
                    </div>
                  )}
                  {c.options?.length > 0 && (
                    <div className="mt-auto pt-6 space-y-5">
                      {c.options.map((o) => (
                        <MerchantChoice
                          key={o.label}
                          option={o}
                          offers={offers}
                          placement="HUB MERCHANT CHOICE"
                          testId={`hub-merchant-${slugify(o.label)}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            );

            if (soleOffer)
              return (
                <AffiliateCardLink
                  key={c.label}
                  href={soleOffer.tracking_url}
                  ariaLabel={`${c.label} — explore at ${soleOffer.merchant}`}
                  testId={`hub-card-${slugify(c.label)}`}
                  trackPayload={{
                    event_type: "cta_click",
                    cta_placement: "HUB SAUNA CARD",
                    offer_category: c.cta.category,
                    offer_id: soleOffer.id,
                    merchant: soleOffer.merchant,
                  }}
                  className={`bg-white flex flex-col ${span}`}
                >
                  {body}
                </AffiliateCardLink>
              );

            return (
              <div
                key={c.label}
                data-testid={`hub-card-${slugify(c.label)}`}
                className={`bg-white flex flex-col group ${span}`}
              >
                {body}
              </div>
            );
          })}
        </div>
      )}

      {group.tiles?.length > 0 && (
        <div
          className={`grid gap-px bg-line mt-px ${
            group.tiles.length + (group.tile_image ? 1 : 0) === 2
              ? "md:grid-cols-2"
              : "sm:grid-cols-2 lg:grid-cols-3"
          }`}
        >
          {group.tiles.map((t) => (
            <div
              key={t.label}
              data-testid={`hub-tile-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-white px-5 py-5 flex flex-col"
            >
              <h4 className="text-[16.5px] font-medium text-charcoal">{t.label}</h4>
              <p className="text-[15.5px] leading-[1.6] text-[#4A4A4A] mt-2">{t.note}</p>
              {t.cta && (
                <div className="mt-auto pt-5">
                  <PathCta
                    cta={t.cta}
                    offers={offers}
                    placement="HUB PATH TILE"
                    testId={`hub-tile-cta-${t.cta.category || "internal"}-${t.label
                      .toLowerCase()
                      .replace(/[^a-z]+/g, "-")}`}
                  />
                </div>
              )}
            </div>
          ))}
          {group.tile_image && (
            <div
              data-testid={`hub-tile-image-${group.id}`}
              className="relative bg-white overflow-hidden min-h-[260px]"
            >
              <SmartImage
                src={group.tile_image.image}
                alt={group.tile_image.image_alt}
                sizes="(min-width:1024px) 50vw, 100vw"
                maxWidth={1024}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}

        </div>
      )}

      {group.note && (
        <p
          data-testid={`hub-paths-note-${group.id}`}
          className="text-[14px] leading-relaxed text-[#4F4B45] mt-6 pl-4 border-l-2 border-sage/50 max-w-3xl"
        >
          {group.note}
        </p>
      )}

      {group.ctas?.length > 0 && (
        <div className="mt-7 flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-7">
          {group.ctas.map((c, i) => (
            <PathCta
              key={i}
              cta={c}
              offers={offers}
              dark={dark}
              placement={dark ? "HUB COMMERCIAL CTA" : "HUB PATH CTA"}
              testId={`hub-path-cta-${group.id}-${i}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export const HubExpandedPaths = ({ groups, testId = "hub-expanded-paths", insertAfter = null }) => {
  const [offers, setOffers] = useState({});
  // One or several blocks may be dropped in after a named group.
  const inserts = (Array.isArray(insertAfter) ? insertAfter : [insertAfter]).filter(Boolean);

  useEffect(() => {
    const cats = [
      ...new Set(
        groups.flatMap((g) =>
          [
            ...(g.ctas || []),
            ...(g.tiles || []).map((t) => t.cta),
            ...(g.cards || []).flatMap((c) => [c.cta, ...(c.options || [])]),
          ]
            .filter(Boolean)
            .map((c) => c.category)
            .filter(Boolean)
        )
      ),
    ];
    Promise.all(
      cats.map((c) =>
        api
          .get("/offers/group", { params: { category: c } })
          .then((r) => [c, r.data.offers || []])
          .catch(() => [c, []])
      )
    ).then((pairs) => setOffers(Object.fromEntries(pairs)));
  }, [groups]);

  return (
    <div data-testid={testId}>
      {groups.map((g) => (
        <Fragment key={g.id}>
          <PathGroup group={g} offers={offers} />
          {inserts
            .filter((i) => i.id === g.id)
            .map((i) => (
              <Fragment key={i.id + (i.key || "")}>{i.node}</Fragment>
            ))}
        </Fragment>
      ))}
    </div>
  );
};
